import React from 'react';
import { motion } from 'framer-motion';
import { HiChatAlt2, HiAcademicCap, HiTrendingUp, HiDocumentText, HiStar, HiCurrencyRupee, HiExternalLink } from 'react-icons/hi';

const ACCENT = '#0f9d74';

const features = [
  { icon: HiChatAlt2, title: 'Daily WhatsApp Quiz', description: 'A fresh quiz every evening delivered right on WhatsApp — no app, no login, no new password.' },
  { icon: HiAcademicCap, title: 'Matched to Your Child', description: 'CBSE, ICSE & State board, Grades 1–10 — aligned to the exact chapters their school is teaching.' },
  { icon: HiTrendingUp, title: 'Adapts as They Learn', description: 'Starts gentle and rises with mastery, so a child is never bored and never overwhelmed.' },
  { icon: HiDocumentText, title: 'Explained Reports', description: 'Every answer explained — with drawn working for number problems, straight to your chat.' },
  { icon: HiStar, title: 'Streaks & Rewards', description: 'Badges, streaks and certificates that turn ten minutes of practice into a habit kids keep.' },
  { icon: HiCurrencyRupee, title: 'Free To Try', description: 'A 7-day free trial — no card. Then affordable plans from ₹99, about ₹3.50 a day.' },
];

const QuizPe = () => {
  return (
    <section id="quizpe" className="relative overflow-hidden" style={{ background: '#edf9f4' }}>
      <div className="absolute inset-0 line-grid pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Top bar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 0 4px rgba(15,157,116,0.15)` }} />
          <span className="mono-label" style={{ color: ACCENT }}>Just Launched</span>
          <span className="mono-label" style={{ color: '#6f8b82' }}>·</span>
          <span className="mono-label" style={{ color: '#6f8b82' }}>quizpe.in</span>
        </div>

        {/* Massive headline */}
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-16 items-start mb-20">
          <div>
            <h2 className="text-display mb-8" style={{ color: '#12332b' }}>
              QuizPe.
              <br />
              <span style={{ color: ACCENT }}>Small quiz, big progress.</span>
            </h2>
            <div className="brutalist-line mb-6" style={{ background: ACCENT }} />
            <p className="text-[17px] leading-[1.7] font-medium max-w-lg" style={{ color: '#4b5f59' }}>
              <strong style={{ color: '#12332b' }}>quizpe.in</strong> is live — a
              <strong style={{ color: ACCENT }}> daily Maths quiz for school children</strong>, delivered
              entirely on WhatsApp. Matched to each child’s board, grade and month, it builds a
              ten-minute evening habit and lands a full, explained report the moment they finish.
            </p>
          </div>

          {/* CTA column */}
          <div className="flex flex-col gap-4 lg:pt-8">
            <a href="https://quizpe.in" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 justify-center font-bold uppercase tracking-[0.06em] text-[13px] py-4 px-6"
              style={{ background: ACCENT, color: '#ffffff' }}>
              Visit quizpe.in <HiExternalLink className="w-4 h-4" />
            </a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-3 font-bold uppercase tracking-[0.06em] text-[13px] py-4 px-6"
              style={{ border: '1.5px solid #12332b', color: '#12332b' }}>
              Build something similar
            </a>
            <div className="flex items-center gap-3 mt-4 px-1">
              <div className="w-8 h-8 flex items-center justify-center" style={{ background: ACCENT }}>
                <HiChatAlt2 className="w-5 h-5" style={{ color: '#ffffff' }} />
              </div>
              <div>
                <p className="text-[12px] font-bold" style={{ color: '#12332b' }}>Meta-verified WhatsApp Business</p>
                <p className="text-[10px] font-medium" style={{ color: '#6f8b82' }}>A real habit, delivered on the app families already use every day</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features — horizontal strip layout */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0" style={{ borderTop: '1px solid #cfe9df' }}>
          {features.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="py-10 px-8 group cursor-default"
                style={{
                  borderRight: (idx % 3 !== 2) ? '1px solid #cfe9df' : 'none',
                  borderBottom: idx < 3 ? '1px solid #cfe9df' : 'none',
                }}>
                <div className="flex items-center gap-3 mb-5">
                  <Icon className="w-5 h-5" style={{ color: ACCENT }} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: '#12332b' }}>
                    {item.title}
                  </span>
                </div>
                <p className="text-[13px] leading-[1.7] font-medium" style={{ color: '#4b5f59' }}>
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuizPe;
